import React from 'react';
import {
    Container,
    Grid,
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Chip,
} from '@mui/material';
import { ChevronRight } from '@mui/icons-material';

interface HeroSectionProps {
    scrollToSection: (id: string) => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ scrollToSection }) => {
    return (
        <Box
            id="home"
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                pt: { xs: 12, md: 16 },
                pb: { xs: 8, md: 12 },
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={4}>
                    {/* Left Column - Main Content */}
                    <Grid size={{ xs: 12, md: 7 }}>
                        <Card
                            sx={{
                                p: { xs: 3, md: 5 },
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                            }}
                        >
                            <CardContent sx={{ p: 0 }}>
                                {/* Heading */}
                                <Typography
                                    variant="h1"
                                    sx={{
                                        mb: 3,
                                        background: 'linear-gradient(135deg, #5b4cdb 0%, #1e293b 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    Expert 11+ Coaching that builds confidence and results.
                                </Typography>

                                {/* Subtitle */}
                                <Typography
                                    variant="body1"
                                    sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.8 }}
                                >
                                    At <strong>Myl </strong>, we specialise in high-quality{' '}
                                    <strong>11+ exam preparation</strong>, helping children develop strong
                                    academic foundations and the confidence needed to succeed in grammar and
                                    selective school entrance exams.
                                </Typography>

                                {/* Feature Pills */}
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 4 }}>
                                    {['Small groups', 'Personalised learning', 'Mock exams', 'Regular feedback'].map(
                                        (feature) => (
                                            <Chip
                                                key={feature}
                                                label={feature}
                                                size="small"
                                                sx={{
                                                    background: 'rgba(91, 76, 219, 0.08)',
                                                    border: '1px solid rgba(91, 76, 219, 0.2)',
                                                    color: '#5b4cdb',
                                                    fontWeight: 600,
                                                }}
                                            />
                                        )
                                    )}
                                </Box>

                                {/* CTA Buttons */}
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        endIcon={<ChevronRight />}
                                        onClick={() => scrollToSection('contact')}
                                    >
                                        Book a Free Consultation
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        onClick={() => scrollToSection('courses')}
                                    >
                                        View Courses
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Right Column - Info Cards */}
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Card sx={{
                                background: 'linear-gradient(135deg, rgba(91, 76, 219, 0.08) 0%, rgba(6, 182, 212, 0.04) 100%)',
                                border: '2px solid rgba(91, 76, 219, 0.2)',
                                boxShadow: '0 8px 24px rgba(91, 76, 219, 0.12)',
                            }}>
                                <CardContent sx={{ p: 3.5 }}>
                                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 800, color: '#1e293b', textAlign: 'center' }}>
                                        📚 Subjects Covered
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {['Maths', 'English', 'Verbal Reasoning', 'Non-Verbal Reasoning'].map((subject) => (
                                            <Box key={subject} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    background: `linear-gradient(135deg, #5b4cdb, #06b6d4)`,
                                                    flexShrink: 0,
                                                }} />
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.15rem' }}>
                                                    {subject}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                        Our Promise
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Structured learning, supportive teaching, and exam-focused practice.
                                    </Typography>
                                </CardContent>
                            </Card>

                            <Card sx={{
                                background: 'linear-gradient(135deg, #5b4cdb 0%, #06b6d4 100%)',
                                boxShadow: '0 8px 24px rgba(91, 76, 219, 0.3)',
                            }}>
                                <CardContent sx={{ p: 3.5 }}>
                                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 800, color: '#ffffff', textAlign: 'center' }}>
                                        📞 Contact
                                    </Typography>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="body2" sx={{
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            mb: 1.5,
                                            fontSize: '0.95rem',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.1em',
                                        }}>
                                            Call / WhatsApp
                                        </Typography>
                                        <Typography
                                            component="a"
                                            href="tel:+447459240172"
                                            variant="h4"
                                            sx={{
                                                fontWeight: 900,
                                                color: '#ffffff',
                                                fontSize: '1.8rem',
                                                letterSpacing: '0.03em',
                                                textDecoration: 'none',
                                                display: 'block',
                                                textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'scale(1.05)',
                                                    textShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                                                },
                                            }}
                                        >
                                            +44 7459 240172
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};
